import { Link, useParams } from "react-router-dom";
import { ArrowLeft, GitCompareArrows } from "lucide-react";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Button } from "@/components/atoms/studio/ui/button";
import { EnginePlanReportView } from "@/components/organisms/migrations/EnginePlanReportView";
import { EngineUnavailable } from "./EnginePlans";
import { useEngineAvailable, useEnginePlanReport } from "@/hooks/migrations/engine";
import { engineErrorMessage } from "@/services/migrations/engine";

/**
 * One engine plan's report.
 *
 * The report is POLLED (see `useEnginePlanReport`), never streamed through
 * `stores/studio/runStore`: that store's `onClose` calls `complete("done")` when a
 * stream drops, which is benign for a playbook whose result is already persisted and
 * unacceptable for a run whose entire product is a verdict — a cut connection would
 * render as a clean finish. Polling can only ever show the status the server returned.
 */
export function EnginePlanDetail() {
  const { planId } = useParams<{ planId: string }>();
  const available = useEngineAvailable();
  const reportQ = useEnginePlanReport(planId);

  return (
    <div className="flex h-full flex-col gap-4 animate-fade-in bg-background">
      <PageHeader
        icon={GitCompareArrows}
        title="Migration Engine"
        description={planId ?? ""}
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/migration-engine">
              <ArrowLeft className="mr-1 h-3.5 w-3.5" />
              All plans
            </Link>
          </Button>
        }
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {available.isLoading ? (
          <p className="font-mono text-xs text-muted-foreground">Checking for the engine…</p>
        ) : available.data !== true ? (
          <EngineUnavailable />
        ) : reportQ.isLoading ? (
          <p className="font-mono text-xs text-muted-foreground">Loading the report…</p>
        ) : reportQ.isError ? (
          // No verdict is not the same as a clean verdict. Say the report could not be
          // read rather than rendering an empty, reassuring page.
          <p className="font-mono text-xs text-destructive">
            This report could not be loaded: {engineErrorMessage(reportQ.error)}
          </p>
        ) : reportQ.data ? (
          <EnginePlanReportView report={reportQ.data} orgId={reportQ.data.org_id} />
        ) : null}
      </div>
    </div>
  );
}

export default EnginePlanDetail;
